/* ============================================================
   Coastal Color landing page — scoped scroll-reveal + add-to-cart.
   Vanilla JS, no dependencies, no globals (single guarded IIFE).
   Progressive enhancement only: if this never runs, all content
   stays fully visible (the reveal CSS is armed by the class this
   script adds) and the add-to-cart forms still work via a normal
   POST to routes.cart_add_url. Respects prefers-reduced-motion and
   works inside the Shopify theme editor (shopify:section:load).

   Add-to-cart prefers window.FmrCart (this theme's shared cart
   helper in fmr-cart.js) so a click here opens the same drawer as
   everywhere else on the site. If that global isn't present it
   falls back to the Shopify Cart AJAX API and dispatches Horizon's
   own `cart:update` event, which the <cart-drawer-component
   auto-open> also listens for — so the drawer still opens rather
   than navigating to /cart. A plain form submit is the last resort
   only if fetch itself fails.
   ============================================================ */
(function () {
  'use strict';

  // Guard so multiple <script> includes (one per section) run once.
  if (window.__lpCoastalColorLoaded) return;
  window.__lpCoastalColorLoaded = true;

  var REVEAL_READY = 'lp-coastal-color--reveal-ready';
  var prefersReduced =
    window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var supportsObserver = 'IntersectionObserver' in window;
  var observer = null;

  function getObserver() {
    if (observer) return observer;
    observer = new IntersectionObserver(
      function (entries) {
        for (var i = 0; i < entries.length; i++) {
          if (entries[i].isIntersecting) {
            entries[i].target.classList.add('is-visible');
            observer.unobserve(entries[i].target);
          }
        }
      },
      { rootMargin: '0px 0px -8% 0px', threshold: 0.08 }
    );
    return observer;
  }

  // Arm reveal for one section root (or find roots within a container).
  function prepare(scope) {
    if (!scope || prefersReduced || !supportsObserver) return;

    var roots;
    if (scope.classList && scope.classList.contains('lp-coastal-color')) {
      roots = [scope];
    } else if (scope.querySelectorAll) {
      roots = scope.querySelectorAll('.lp-coastal-color');
    } else {
      return;
    }

    Array.prototype.forEach.call(roots, function (root) {
      if (root.getAttribute('data-lpcc-ready') === 'true') return;
      root.setAttribute('data-lpcc-ready', 'true');
      root.classList.add(REVEAL_READY);

      var items = root.querySelectorAll('[data-lpcc-reveal]');
      Array.prototype.forEach.call(items, function (item, index) {
        // Gentle stagger, capped so later items don't lag.
        item.style.transitionDelay = Math.min(index * 70, 280) + 'ms';
        getObserver().observe(item);
      });
    });
  }

  function init() {
    prepare(document);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Re-arm sections added/re-rendered in the theme editor.
  document.addEventListener('shopify:section:load', function (event) {
    prepare(event.target);
  });

  /* ---------- Add to cart ---------- */

  function setButtonState(button, state, label) {
    if (!button) return;
    if (!button.dataset.lpccOriginalLabel) {
      button.dataset.lpccOriginalLabel = button.textContent.trim();
    }

    if (state === 'loading') {
      button.disabled = true;
      button.textContent = label || 'Adding…';
    } else if (state === 'added') {
      button.disabled = false;
      button.textContent = label || 'Added!';
      window.setTimeout(function () {
        button.textContent = button.dataset.lpccOriginalLabel;
      }, 1600);
    } else {
      button.disabled = false;
      button.textContent = button.dataset.lpccOriginalLabel;
    }
  }

  function submitAddForm(form) {
    var button = form.querySelector('[data-lpcc-add-button]');
    var idField = form.querySelector('[name="id"]');
    var quantityField = form.querySelector('[name="quantity"]');
    var variantId = idField && idField.value;
    var quantity = (quantityField && quantityField.value) || '1';

    if (!variantId) return;

    setButtonState(button, 'loading', form.getAttribute('data-lpcc-loading-label'));

    var addedLabel = form.getAttribute('data-lpcc-added-label');
    var qty = parseInt(quantity, 10) || 1;

    // Preferred path: the theme's shared helper (fmr-cart.js). It POSTs,
    // refetches the cart and dispatches Horizon's `cart:update`, which the
    // <cart-drawer-component auto-open> reacts to by opening the drawer.
    // We pass no button so our own label handling below stays in charge.
    if (window.FmrCart && typeof window.FmrCart.add === 'function') {
      window.FmrCart.add(variantId, qty, null).then(function (cart) {
        setButtonState(button, cart ? 'added' : 'idle', addedLabel);
      });
      return;
    }

    // Fallback (FmrCart not loaded): add via the Cart AJAX API ourselves,
    // then fetch the fresh cart and fire the same `cart:update` event so the
    // drawer opens the same way — a successful add never redirects to /cart.
    fetch('/cart/add.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: [{ id: variantId, quantity: qty }] })
    })
      .then(function (response) {
        if (!response.ok) throw new Error('Cart add failed');
        return fetch('/cart.js', { headers: { Accept: 'application/json' } });
      })
      .then(function (response) {
        return response.json();
      })
      .then(function (cart) {
        document.dispatchEvent(
          new CustomEvent('cart:update', {
            bubbles: true,
            detail: {
              resource: cart,
              data: { didError: false, source: 'lp-coastal-color', itemCount: cart.item_count }
            }
          })
        );
        document.dispatchEvent(new CustomEvent('lp-coastal-color:cart-add', { detail: cart }));
        setButtonState(button, 'added', addedLabel);
      })
      .catch(function () {
        setButtonState(button, 'idle');
        form.submit();
      });
  }

  document.addEventListener('submit', function (event) {
    var form = event.target.closest && event.target.closest('[data-lpcc-add-form]');
    if (!form) return;
    event.preventDefault();
    submitAddForm(form);
  });
})();
